import { useMorthandlingCommandPost } from '@restDataAccess/morthandling/morthandling.utils.ts';
import {
  AddMorthandlingPlcRequest,
  MorthandlingCommandEndpoints,
  MorthandlingPlcEndpoints,
  MorthandlingPlcInfo,
  UpdateDeadFishUnitConfigRequest,
} from '@scaleaq/sitecontrol-lib';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  ServiceQueryOptions,
  useAuthApiDeleteById,
  useAuthApiGetSingleFetch,
  useAuthApiPost,
  useAuthApiUpdateById,
} from '@/dataAccess/rest';

export const useAddNewMorthandlingPLC = (identifier?: string) =>
  useAuthApiPost<AddMorthandlingPlcRequest>({
    baseApiRoute: API_NAMES.morthandlingApi,
    route: MorthandlingPlcEndpoints.addPlc,
    cacheKeys: [CACHE_KEYS.morthandlingPlcs],
    identifier,
  });

export const useGetMorthandlingPLCs = ({ enabled }: ServiceQueryOptions) =>
  useAuthApiGetSingleFetch<MorthandlingPlcInfo[]>({
    baseApiRoute: API_NAMES.morthandlingApi,
    route: MorthandlingPlcEndpoints.getAllPlcs,
    cacheKeys: [CACHE_KEYS.morthandlingPlcs],
    enabled,
  });

export const useUpdateMorthandlingPLC = (identifier?: string) =>
  useAuthApiUpdateById<UpdateDeadFishUnitConfigRequest & { id: string }>({
    baseApiRoute: API_NAMES.morthandlingApi,
    route: MorthandlingPlcEndpoints.configuration,
    cacheKeys: [CACHE_KEYS.morthandlingPlcs],
    identifier,
    putOrPatch: 'PATCH',
  });

export const useRemoveMorthandlingPLC = (identifier?: string) =>
  useAuthApiDeleteById<MorthandlingPlcInfo & { id: string }>({
    baseApiRoute: API_NAMES.morthandlingApi,
    route: MorthandlingPlcEndpoints.deletePlcByInventoryId,
    cacheKeys: [CACHE_KEYS.morthandlingPlcs],
    identifier,
  });

//****************************************
// MortHandling Commands
//****************************************

export const useSetDeadFishMode = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.setDeadFishMode, 'setDeadFishMode');

export const usePenStartPumping = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.penStartPumping, 'penStartPumping');

export const usePenStopPumping = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.penStopPumping, 'penStopPumping');

export const useLiftUpRequestRemoteControl = () =>
  useMorthandlingCommandPost(
    MorthandlingCommandEndpoints.liftUpRequestRemoteControl,
    'liftUpRequestRemoteControl'
  );

export const useFlusherStart = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.flusherStart, 'flusherStart');

export const useFlusherStop = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.flusherStop, 'flusherStop');

export const useConveyorBeltStart = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.conveyorBeltStart, 'conveyorBeltStart');

export const useConveyorBeltStop = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.conveyorBeltStop, 'conveyorBeltStop');

export const useStunnerStart = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.stunnerStart, 'stunnerStart');

export const useStunnerStop = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.stunnerStop, 'stunnerStop');

export const useEnsilageStartFill = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.ensilageStartFill, 'ensilageStartFill');

export const useEnsilageStopFill = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.ensilageStopFill, 'ensilageStopFill');

export const useEnsilageRequestRemoteMode = () =>
  useMorthandlingCommandPost(
    MorthandlingCommandEndpoints.ensilageRequestRemoteMode,
    'ensilageRequestRemoteMode'
  );

export const useEnsilageRequestStart = () =>
  useMorthandlingCommandPost(
    MorthandlingCommandEndpoints.ensilageRequestStart,
    'ensilageRequestStart'
  );

export const useAcknowledgeAlarm = () =>
  useMorthandlingCommandPost(MorthandlingCommandEndpoints.acknowledgeAlarm, 'acknowledgeAlarm');
